import axios from "../axios-client/instance";
import { ApiResponse } from "../shared/types";

// ==================== TYPES ====================

export interface MultilingualText {
  en?: string;
  fr?: string;
  ko?: string;
  vi?: string;
  zh?: string;
  th?: string;
  es?: string;
  ru?: string;
  hi?: string;
}

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "ko", label: "한국인" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "zh", label: "中文" },
  { code: "th", label: "แบบไทย" },
  { code: "es", label: "Español" },
  { code: "ru", label: "Русский" },
  { code: "hi", label: "हिन्दी" },
] as const;

export type SupportedLanguageCode =
  (typeof SUPPORTED_LANGUAGES)[number]["code"];

export enum QuestionType {
  SHORT_TEXT = "short_text",
  LONG_TEXT = "long_text",
  DATE = "date",
  MULTIPLE_CHOICE = "multiple_choice",
  MULTI_SELECT = "multi_select",
  RATING = "rating",
}

export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  [QuestionType.SHORT_TEXT]: "Short Text",
  [QuestionType.LONG_TEXT]: "Long Text",
  [QuestionType.DATE]: "Date",
  [QuestionType.MULTIPLE_CHOICE]: "Multiple Choice (Single Select)",
  [QuestionType.MULTI_SELECT]: "Multiple Select (Checkboxes)",
  [QuestionType.RATING]: "Rating (1-10 Stars)",
};

export interface QuestionOption {
  id?: number;
  text: MultilingualText;
  order: number;
  isOtherOption?: boolean;
}

export interface SurveyQuestion {
  id?: number;
  title: MultilingualText;
  hintText?: MultilingualText;
  type: QuestionType;
  order: number;
  isRequired: boolean;
  answerCount?: number; // Number of answer boxes for short text questions
  maxSelections?: number | null; // Max options selectable for multi-select questions (null = no limit)
  sectionId?: number;
  options: QuestionOption[];
  // Conditional question logic
  conditionQuestionId?: number;
  conditionOptionIds?: number[];
}

export interface SurveySection {
  id?: number;
  title?: MultilingualText | null;
  order: number;
  questions: SurveyQuestion[];
}

export interface SurveyCreatedBy {
  id: number;
  username: string;
  firstName?: string;
  lastName?: string;
}

export interface SurveyListItem {
  id: number;
  publicId: string;
  title: MultilingualText;
  isActive: boolean;
  channels?: string[];
  createdAt: string;
  updatedAt: string;
  questionCount: number;
  responseCount: number;
  createdBy?: SurveyCreatedBy;
}

export interface SurveyDetail {
  id: number;
  publicId: string;
  title: MultilingualText;
  welcomeMessage?: MultilingualText;
  successMessage?: MultilingualText;
  successButtonLabel?: MultilingualText;
  successButtonUrl?: string;
  isActive: boolean;
  supportedLanguages: string[];
  channels?: string[];
  backgroundImageUrl?: string;
  logoImageUrl?: string;
  createdAt: string;
  updatedAt: string;
  sections: SurveySection[];
  questions: SurveyQuestion[]; // Legacy questions not in a section
}

export interface PublicSurvey {
  publicId: string;
  title: MultilingualText;
  welcomeMessage?: MultilingualText;
  successMessage?: MultilingualText;
  successButtonLabel?: MultilingualText;
  successButtonUrl?: string;
  supportedLanguages: string[];
  backgroundImageUrl?: string;
  logoImageUrl?: string;
  sections: SurveySection[];
  questions: SurveyQuestion[]; // Legacy questions not in a section
}

export interface QuestionStats {
  questionId: number;
  questionTitle: MultilingualText;
  type: QuestionType;
  optionCounts?: Record<string, number>;
  textResponses?: string[];
  dateResponses?: string[];
  averageRating?: number;
  ratingDistribution?: Record<number, number>;
  totalResponses: number;
}

export interface SurveyStats {
  surveyId: number;
  surveyTitle: MultilingualText;
  totalResponses: number;
  byLanguage: Record<string, number>;
  dailySubmissions: Record<string, number>;
  questionStats: QuestionStats[];
}

export interface SurveyResponseAnswer {
  id: number;
  questionId: number;
  textValue?: string;
  dateValue?: string;
  selectedOptionId?: number;
  selectedOptionIds?: number[];
  ratingValue?: number;
}

export interface SurveyResponseItem {
  id: number;
  surveyId: number;
  language: string;
  channel?: string;
  ipAddress?: string;
  userAgent?: string;
  submittedAt: string;
  answers: SurveyResponseAnswer[];
}

// ==================== REQUEST TYPES ====================

export interface CreateQuestionOptionRequest {
  id?: number; // Include ID when updating existing options
  text: MultilingualText;
  order?: number;
  isOtherOption?: boolean;
}

export interface CreateQuestionRequest {
  title: MultilingualText;
  hintText?: MultilingualText | null; // null clears a previously saved hint
  type: QuestionType;
  sectionId?: number;
  order?: number;
  isRequired?: boolean;
  answerCount?: number; // Number of answer boxes for short text questions (default: 1)
  maxSelections?: number | null; // Max options selectable for multi-select questions (null = no limit)
  options?: CreateQuestionOptionRequest[];
  // Conditional question logic
  conditionQuestionId?: number | null;
  conditionOptionIds?: number[] | null;
}

export interface CreateSectionRequest {
  title?: MultilingualText;
  order?: number;
}

export interface UpdateSectionRequest {
  title?: MultilingualText;
  order?: number;
}

export interface CreateSurveyRequest {
  title: MultilingualText;
  welcomeMessage?: MultilingualText;
  successMessage?: MultilingualText;
  successButtonLabel?: MultilingualText;
  successButtonUrl?: string;
  isActive?: boolean;
  supportedLanguages?: string[];
  channels?: string[];
  backgroundImageUrl?: string;
  logoImageUrl?: string;
  questions?: CreateQuestionRequest[];
}

export interface UpdateSurveyRequest {
  title?: MultilingualText;
  welcomeMessage?: MultilingualText;
  successMessage?: MultilingualText;
  successButtonLabel?: MultilingualText;
  successButtonUrl?: string;
  isActive?: boolean;
  supportedLanguages?: string[];
  channels?: string[];
  backgroundImageUrl?: string;
  logoImageUrl?: string;
}

export interface SubmitAnswerRequest {
  questionId: number;
  textValue?: string;
  dateValue?: number; // Unix timestamp
  selectedOptionId?: number;
  selectedOptionIds?: number[];
  ratingValue?: number;
}

export interface SubmitSurveyRequest {
  language?: string;
  channel?: string;
  answers: SubmitAnswerRequest[];
  turnstileToken: string; // Cloudflare Turnstile token, verified server-side
}

// ==================== EXPORT/IMPORT TYPES ====================

export interface ExportQuestionOption {
  text: MultilingualText;
  order: number;
  isOtherOption?: boolean;
}

export interface ExportQuestion {
  title: MultilingualText;
  hintText?: MultilingualText;
  type: QuestionType;
  order: number;
  isRequired: boolean;
  answerCount?: number;
  maxSelections?: number | null;
  options?: ExportQuestionOption[];
  // Conditional question logic (order-based for import/export)
  conditionQuestionOrder?: number;
  conditionOptionOrders?: number[];
}

export interface ExportSection {
  title?: MultilingualText | null;
  order: number;
  questions: ExportQuestion[];
}

export interface SurveyExport {
  version: string;
  exportedAt: string;
  title: MultilingualText;
  welcomeMessage?: MultilingualText;
  successMessage?: MultilingualText;
  isActive: boolean;
  supportedLanguages: string[];
  channels?: string[];
  sections: ExportSection[];
  questions?: ExportQuestion[];
}

export interface SurveyImportRequest {
  version?: string;
  title: MultilingualText;
  welcomeMessage?: MultilingualText;
  successMessage?: MultilingualText;
  isActive?: boolean;
  supportedLanguages?: string[];
  channels?: string[];
  sections: ExportSection[];
  questions?: ExportQuestion[];
}

// ==================== API CLIENT ====================

const surveyApi = {
  // ==================== ADMIN ENDPOINTS ====================

  // Get all surveys (optionally filtered by channel)
  async getAll(channel?: string) {
    const params = channel ? { channel } : {};
    const { data } = await axios.get<ApiResponse<SurveyListItem[]>>(
      "/admin/surveys",
      { params }
    );
    return data;
  },

  // Get all unique channels
  async getChannels() {
    const { data } = await axios.get<ApiResponse<string[]>>(
      "/admin/surveys/channels"
    );
    return data;
  },

  // Get survey by ID
  async getById(id: number) {
    const { data } = await axios.get<ApiResponse<SurveyDetail>>(
      `/admin/surveys/${id}`
    );
    return data;
  },

  // Create a new survey
  async create(request: CreateSurveyRequest) {
    const { data } = await axios.post<ApiResponse<SurveyDetail>>(
      "/admin/surveys",
      request
    );
    return data;
  },

  // Update a survey
  async update(id: number, request: UpdateSurveyRequest) {
    const { data } = await axios.put<ApiResponse<SurveyDetail>>(
      `/admin/surveys/${id}`,
      request
    );
    return data;
  },

  // Delete a survey
  async delete(id: number) {
    const { data } = await axios.post<ApiResponse<void>>(
      `/admin/surveys/${id}/delete`
    );
    return data;
  },

  // ==================== SECTION ENDPOINTS ====================

  // Add section to survey
  async addSection(surveyId: number, request: CreateSectionRequest) {
    const { data } = await axios.post<ApiResponse<SurveySection>>(
      `/admin/surveys/${surveyId}/sections`,
      request
    );
    return data;
  },

  // Update section
  async updateSection(sectionId: number, request: UpdateSectionRequest) {
    const { data } = await axios.put<ApiResponse<SurveySection>>(
      `/admin/surveys/sections/${sectionId}`,
      request
    );
    return data;
  },

  // Delete section
  async deleteSection(sectionId: number) {
    const { data } = await axios.post<ApiResponse<void>>(
      `/admin/surveys/sections/${sectionId}/delete`
    );
    return data;
  },

  // Reorder sections
  async reorderSections(
    surveyId: number,
    sectionOrder: Array<{ id: number; order: number }>
  ) {
    const { data } = await axios.put<ApiResponse<void>>(
      `/admin/surveys/${surveyId}/sections/reorder`,
      { sections: sectionOrder }
    );
    return data;
  },

  // ==================== QUESTION ENDPOINTS ====================

  // Add question to survey
  async addQuestion(surveyId: number, request: CreateQuestionRequest) {
    const { data } = await axios.post<ApiResponse<SurveyQuestion>>(
      `/admin/surveys/${surveyId}/questions`,
      request
    );
    return data;
  },

  // Update question
  async updateQuestion(
    questionId: number,
    request: Partial<CreateQuestionRequest>
  ) {
    const { data } = await axios.put<ApiResponse<SurveyQuestion>>(
      `/admin/surveys/questions/${questionId}`,
      request
    );
    return data;
  },

  // Delete question
  async deleteQuestion(questionId: number) {
    const { data } = await axios.post<ApiResponse<void>>(
      `/admin/surveys/questions/${questionId}/delete`
    );
    return data;
  },

  // Reorder questions
  async reorderQuestions(
    surveyId: number,
    questionOrder: Array<{ id: number; order: number }>
  ) {
    const { data } = await axios.put<ApiResponse<void>>(
      `/admin/surveys/${surveyId}/questions/reorder`,
      { questions: questionOrder }
    );
    return data;
  },

  // Get survey stats
  async getStats(surveyId: number, channel?: string) {
    const params: { channel?: string } = {};
    if (channel) {
      params.channel = channel;
    }
    const { data } = await axios.get<ApiResponse<SurveyStats>>(
      `/admin/surveys/${surveyId}/stats`,
      { params }
    );
    return data;
  },

  // Export Excel (XLSX)
  async exportXLSX(surveyId: number, surveyName: string, channel?: string) {
    const params: { channel?: string } = {};
    if (channel) {
      params.channel = channel;
    }
    const response = await axios.get(`/admin/surveys/${surveyId}/xlsx`, {
      responseType: "blob",
      params,
    });

    const filename = channel
      ? `survey-${surveyName}-${channel}-responses.xlsx`
      : `survey-${surveyName}-responses.xlsx`;

    const href = window.URL.createObjectURL(response.data);
    const anchorElement = document.createElement("a");
    anchorElement.href = href;
    anchorElement.download = filename;
    document.body.appendChild(anchorElement);
    anchorElement.click();
    document.body.removeChild(anchorElement);
    window.URL.revokeObjectURL(href);
  },

  // Get paginated responses for a survey
  async getResponses(
    surveyId: number,
    page: number = 1,
    perPage: number = 30,
    channel?: string
  ) {
    const params: { page: number; perPage: number; channel?: string } = {
      page,
      perPage,
    };
    if (channel) {
      params.channel = channel;
    }
    const { data } = await axios.get<
      ApiResponse<{ items: SurveyResponseItem[]; total: number }>
    >(`/admin/surveys/${surveyId}/responses`, {
      params,
    });
    return data;
  },

  // Delete a response
  async deleteResponse(responseId: number) {
    const { data } = await axios.post<ApiResponse<void>>(
      `/admin/surveys/responses/${responseId}/delete`
    );
    return data;
  },

  // ==================== EXPORT/IMPORT ENDPOINTS ====================

  // Export survey structure as JSON file
  async exportSurvey(surveyId: number, surveyPublicId: string) {
    const response = await axios.get(`/admin/surveys/${surveyId}/export`, {
      responseType: "blob",
    });

    const href = window.URL.createObjectURL(response.data);
    const anchorElement = document.createElement("a");
    anchorElement.href = href;
    anchorElement.download = `survey-${surveyPublicId}-export.json`;
    document.body.appendChild(anchorElement);
    anchorElement.click();
    document.body.removeChild(anchorElement);
    window.URL.revokeObjectURL(href);
  },

  // Import survey from JSON
  async importSurvey(surveyData: SurveyImportRequest) {
    const { data } = await axios.post<ApiResponse<SurveyDetail>>(
      "/admin/surveys/import",
      surveyData
    );
    return data;
  },

  // ==================== TRANSLATION EXPORT/IMPORT ENDPOINTS ====================

  // Export translations as Excel file
  async exportTranslations(surveyId: number, surveyPublicId: string) {
    const response = await axios.get(
      `/admin/surveys/${surveyId}/translations/export`,
      {
        responseType: "blob",
      }
    );

    const href = window.URL.createObjectURL(response.data);
    const anchorElement = document.createElement("a");
    anchorElement.href = href;
    anchorElement.download = `survey-${surveyPublicId}-translations.xlsx`;
    document.body.appendChild(anchorElement);
    anchorElement.click();
    document.body.removeChild(anchorElement);
    window.URL.revokeObjectURL(href);
  },

  // Import translations from Excel file
  async importTranslations(surveyId: number, file: File) {
    const formData = new FormData();
    formData.append("file", file);

    const { data } = await axios.post<
      ApiResponse<{
        updated: number;
        skipped: number;
        errors: string[];
      }>
    >(`/admin/surveys/${surveyId}/translations/import`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  },

  // ==================== BRANDING UPLOAD ENDPOINTS ====================

  // Upload branding image (logo or background)
  async uploadBrandingImage(file: File) {
    const formData = new FormData();
    formData.append("file", file);

    const { data } = await axios.post<ApiResponse<{ url: string }>>(
      "/admin/upload/branding",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  },

  // ==================== OWNERSHIP MANAGEMENT ====================

  // Get all users for dropdown (admin only)
  async getUsersForDropdown() {
    const { data } = await axios.get<
      ApiResponse<
        Array<{
          id: number;
          username: string;
          firstName?: string;
          lastName?: string;
        }>
      >
    >("/admin/surveys/users/dropdown");
    return data;
  },

  // Change survey owner (admin only)
  async changeOwner(surveyId: number, newOwnerId: number) {
    const { data } = await axios.post<ApiResponse<SurveyDetail>>(
      `/admin/surveys/${surveyId}/change-owner`,
      { newOwnerId }
    );
    return data;
  },

  // ==================== PUBLIC ENDPOINTS ====================

  // Get public survey by ID (legacy - no channel)
  async getPublicSurvey(publicId: string, internalTest = false) {
    const params = internalTest ? { internal_test: "1" } : {};
    const { data } = await axios.get<ApiResponse<PublicSurvey>>(
      `/surveys/${publicId}`,
      { requiresAuth: false, params }
    );
    return data;
  },

  // Get public survey by channel and ID
  async getPublicSurveyByChannel(
    channel: string,
    publicId: string,
    internalTest = false
  ) {
    const params = internalTest ? { internal_test: "1" } : {};
    const { data } = await axios.get<ApiResponse<PublicSurvey>>(
      `/surveys/${channel}/${publicId}`,
      { requiresAuth: false, params }
    );
    return data;
  },

  // Submit survey response (legacy - no channel)
  async submit(
    publicId: string,
    request: SubmitSurveyRequest,
    internalTest = false
  ) {
    const params = internalTest ? { internal_test: "1" } : {};
    const { data } = await axios.post<ApiResponse<void>>(
      `/surveys/${publicId}/submit`,
      request,
      { requiresAuth: false, params }
    );
    return data;
  },

  // Submit survey response by channel
  async submitByChannel(
    channel: string,
    publicId: string,
    request: SubmitSurveyRequest,
    internalTest = false
  ) {
    const params = internalTest ? { internal_test: "1" } : {};
    const { data } = await axios.post<ApiResponse<void>>(
      `/surveys/${channel}/${publicId}/submit`,
      request,
      { requiresAuth: false, params }
    );
    return data;
  },
};

export default surveyApi;
